import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Handshake, Calendar, MapPin, CurrencyDollar, Buildings, Heart, Plus, TrashSimple,
  Megaphone, CheckCircle, ArrowRight, ShareNetwork, Receipt, Star, Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { withUtm } from "../utils/utm";

const SC_COUNTIES = [
  "Abbeville","Aiken","Allendale","Anderson","Bamberg","Barnwell","Beaufort",
  "Berkeley","Calhoun","Charleston","Cherokee","Chester","Chesterfield",
  "Clarendon","Colleton","Darlington","Dillon","Dorchester","Edgefield",
  "Fairfield","Florence","Georgetown","Greenville","Greenwood","Hampton",
  "Horry","Jasper","Kershaw","Lancaster","Laurens","Lee","Lexington",
  "McCormick","Marion","Marlboro","Newberry","Oconee","Orangeburg",
  "Pickens","Richland","Saluda","Spartanburg","Sumter","Union",
  "Williamsburg","York",
];

const INDUSTRIES = [
  "Banking & Finance","Healthcare","Hospitality & Tourism","Insurance","Real Estate",
  "Restaurant & Food Service","Retail","Construction","Manufacturing","Auto Dealership",
  "Law / Professional Services","Tech / Software","Utilities","Energy","Telecom",
];

const SPONSORSHIP_CATEGORIES = [
  "Youth & Education","Food Security","Faith-Based","Health & Wellness",
  "Arts & Culture","Environment","Animals","Veterans & Military Families",
  "Housing & Homelessness","Workforce & Economic Development",
  "Disability Services","Senior Services","General",
];

const Sponsorships = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("browse"); // browse | mine
  const [opportunities, setOpportunities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featuredWeek, setFeaturedWeek] = useState("");
  const [categories, setCategories] = useState([]); // [{name, count}, ...]
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ county: "", industry: "", maxBudget: "", category: "" });

  const [showPost, setShowPost] = useState(false);
  const [interestOpp, setInterestOpp] = useState(null);

  // Post form
  const [form, setForm] = useState({
    org_name: user?.nonprofit_profile?.org_name || "",
    event_name: "",
    description: "",
    county: user?.nonprofit_profile?.county || "",
    category: "General",
    sponsorship_min: "500",
    sponsorship_max: "5000",
    deadline: "",
    industries_welcomed: [],
    benefits_offered: ["Logo on event signage","Social media spotlight"],
    contact_email: user?.email || "",
  });
  const [postLoading, setPostLoading] = useState(false);

  // Interest form
  const [interestForm, setInterestForm] = useState({
    business_name: "",
    business_email: user?.email || "",
    business_phone: "",
    proposed_tier: "",
    message: "",
  });
  const [interestLoading, setInterestLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.county) params.set("county", filters.county);
      if (filters.industry) params.set("industry", filters.industry);
      if (filters.maxBudget) params.set("max_budget", filters.maxBudget);
      if (filters.category) params.set("category", filters.category);
      const [pub, m, cats] = await Promise.all([
        axios.get(`${API}/sponsorships?${params.toString()}`),
        user ? axios.get(`${API}/sponsorships/mine`) : Promise.resolve({ data: { opportunities: [] } }),
        axios.get(`${API}/sponsorships/categories`),
      ]);
      setOpportunities(pub.data.opportunities || []);
      // Only show featured on the unfiltered browse view (avoids confusion when filtering)
      const noFilters = !filters.county && !filters.industry && !filters.maxBudget && !filters.category;
      setFeatured(noFilters ? (pub.data.featured || []) : []);
      setFeaturedWeek(pub.data.featured_week || "");
      setMine(m.data.opportunities || []);
      setCategories(cats.data.categories || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load opportunities");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.county, filters.industry, filters.maxBudget, filters.category]);

  const submitPost = async () => {
    setPostLoading(true);
    try {
      await axios.post(`${API}/sponsorships`, {
        ...form,
        sponsorship_min: parseInt(form.sponsorship_min || "0", 10),
        sponsorship_max: parseInt(form.sponsorship_max || "0", 10),
      });
      toast.success("Opportunity posted — local businesses can now find it");
      setShowPost(false);
      setForm({ ...form, event_name: "", description: "", deadline: "" });
      load();
      setTab("mine");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post");
    } finally { setPostLoading(false); }
  };

  const deleteOpp = async (id) => {
    if (!window.confirm("Delete this opportunity? Local businesses will no longer see it.")) return;
    try {
      await axios.delete(`${API}/sponsorships/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const submitInterest = async () => {
    setInterestLoading(true);
    try {
      await axios.post(`${API}/sponsorships/${interestOpp.opportunity_id}/interest`, interestForm);
      toast.success(`${interestOpp.org_name} will get your details by email`);
      setInterestOpp(null);
      setInterestForm({ business_name: "", business_email: user?.email || "", business_phone: "", proposed_tier: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setInterestLoading(false); }
  };

  const shareOpp = async (opportunityId) => {
    const url = withUtm(`${window.location.origin}/sponsor/${opportunityId}`, "share", "social", `sponsor_${opportunityId}`);
    try {
      if (navigator.share) {
        await navigator.share({ title: "FlashGrants sponsorship opportunity", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it on Facebook, newsletters, anywhere");
    } catch {
      // user dismissed share sheet — no toast
    }
  };

  const list = tab === "mine" ? mine : opportunities;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto py-6" data-testid="sponsorships-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <Handshake weight="fill" className="w-7 h-7 text-emerald-400" />
              Local Sponsorship Opportunities
            </h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Nonprofits post one-off events and programs looking for local business sponsors. Small businesses and franchisees browse and book sponsorships in their county and budget. Free for both sides.
            </p>
          </div>
          {user && (
            <div className="flex gap-2">
              <Button
                onClick={() => (window.location.href = "/giving-log")}
                variant="outline"
                className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm"
                data-testid="giving-log-link"
                title="Quarterly Impact PDFs for audit-ready franchise compliance"
              >
                <Receipt className="w-4 h-4 mr-1" /> Giving Log
              </Button>
              <Button
                onClick={() => setShowPost(true)}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="post-opportunity-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Post Opportunity
              </Button>
            </div>
          )}
        </div>

        {/* Local Hero upsell banner — keeps the ladder visible */}
        <Card className="bg-gradient-to-r from-emerald-500/10 to-zinc-950 border-emerald-500/30">
          <CardContent className="py-4 flex items-start gap-3">
            <Megaphone weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-zinc-200">
                <strong>Sponsoring the same nonprofit twice in a year?</strong> Upgrade to{" "}
                <a href="/local-hero" className="text-emerald-400 hover:text-emerald-300 underline">
                  Local Hero
                </a>{" "}
                for $5K/yr — named recognition, custom microsite, quarterly impact PDFs, and leaderboard placement.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800">
          <button
            onClick={() => setTab("browse")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "browse" ? "border-emerald-400 text-emerald-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="tab-browse"
          >
            Browse Opportunities ({opportunities.length})
          </button>
          {user && (
            <button
              onClick={() => setTab("mine")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === "mine" ? "border-emerald-400 text-emerald-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid="tab-mine"
            >
              My Opportunities ({mine.length})
            </button>
          )}
        </div>

        {/* Category pills — narrow into a niche so small orgs surface in their own lane */}
        {tab === "browse" && categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="category-pills">
            <button
              onClick={() => setFilters({ ...filters, category: "" })}
              className={`text-xs rounded-sm px-2.5 py-1 transition-colors ${
                !filters.category
                  ? "bg-emerald-500 text-black font-bold"
                  : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
              }`}
              data-testid="cat-pill-all"
            >
              All categories
            </button>
            {categories.filter((c) => c.count > 0).map((c) => (
              <button
                key={c.name}
                onClick={() => setFilters({ ...filters, category: filters.category === c.name ? "" : c.name })}
                className={`text-xs rounded-sm px-2.5 py-1 transition-colors flex items-center gap-1 ${
                  filters.category === c.name
                    ? "bg-emerald-500 text-black font-bold"
                    : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
                }`}
                data-testid={`cat-pill-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.name}
                <span className="text-[10px] opacity-70">({c.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Featured this week — guarantees rotating spotlight for newer/smaller orgs */}
        {tab === "browse" && featured.length > 0 && (
          <div data-testid="featured-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono flex items-center gap-1.5">
                <Star weight="fill" className="w-3.5 h-3.5" /> Featured this week
              </h2>
              <p className="text-[10px] text-zinc-600 font-mono hidden sm:block">Rotates {featuredWeek}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {featured.map((o) => (
                <button
                  key={o.opportunity_id}
                  onClick={() => setInterestOpp(o)}
                  className="text-left bg-gradient-to-br from-emerald-500/10 to-zinc-950 border border-emerald-500/30 rounded-sm p-3 hover:border-emerald-500/60 transition-colors"
                  data-testid={`featured-${o.opportunity_id}`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-mono mb-1">{o.category || "General"}</p>
                  <p className="text-sm font-bold text-zinc-100 line-clamp-2 mb-1">{o.event_name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{o.org_name} · {o.county}</p>
                  <p className="text-xs text-emerald-300 font-mono mt-2">${o.sponsorship_min.toLocaleString()}–${o.sponsorship_max.toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters (browse only) */}
        {tab === "browse" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="filters">
            <select
              value={filters.county}
              onChange={(e) => setFilters({ ...filters, county: e.target.value })}
              className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200"
              data-testid="filter-county"
            >
              <option value="">All counties</option>
              {SC_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filters.industry}
              onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
              className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200"
              data-testid="filter-industry"
            >
              <option value="">All industries</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <Input
              type="number"
              min="0"
              value={filters.maxBudget}
              onChange={(e) => setFilters({ ...filters, maxBudget: e.target.value })}
              placeholder="Max budget ($)"
              className="bg-zinc-900 border-zinc-800 text-sm"
              data-testid="filter-budget"
            />
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="py-12 text-center text-zinc-500">Loading…</div>
        ) : list.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Heart className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">
                {tab === "mine"
                  ? "You haven't posted any opportunities yet."
                  : "No open opportunities match these filters. Try widening your search."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="opportunities-grid">
            {list.map((o) => (
              <Card key={o.opportunity_id} className="bg-zinc-950 border-zinc-800 flex flex-col" data-testid={`opp-card-${o.opportunity_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{o.event_name}</CardTitle>
                    {o.category && o.category !== "General" && (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-sm px-1.5 py-0.5 flex-shrink-0 font-mono whitespace-nowrap">
                        <Tag className="w-2.5 h-2.5 inline mr-0.5" />{o.category}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{o.org_name}</p>
                </CardHeader>
                <CardContent className="space-y-2 flex-1 flex flex-col">
                  <p className="text-sm text-zinc-300 leading-relaxed line-clamp-4">{o.description}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400 pt-2">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{o.county} County</span>
                    <span className="inline-flex items-center gap-1"><CurrencyDollar className="w-3.5 h-3.5 text-emerald-400" />${o.sponsorship_min.toLocaleString()}–${o.sponsorship_max.toLocaleString()}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Deadline: {o.deadline}</span>
                  </div>
                  {o.industries_welcomed?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {o.industries_welcomed.slice(0, 4).map((ind) => (
                        <span key={ind} className="text-[10px] bg-zinc-900 border border-zinc-800 rounded-sm px-1.5 py-0.5 text-zinc-400 uppercase tracking-wider">
                          {ind}
                        </span>
                      ))}
                    </div>
                  )}
                  {o.benefits_offered?.length > 0 && (
                    <div className="pt-2">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-0.5">Sponsor benefits</p>
                      <ul className="text-xs text-zinc-300 space-y-0.5">
                        {o.benefits_offered.slice(0, 3).map((b) => (
                          <li key={b} className="flex items-start gap-1"><CheckCircle weight="fill" className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-auto pt-3 flex gap-2">
                    {tab === "mine" ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => shareOpp(o.opportunity_id)}
                          className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10 rounded-sm flex-1"
                          data-testid={`share-${o.opportunity_id}`}
                          title="Copy a public link to share on Facebook, newsletters, anywhere"
                        >
                          <ShareNetwork className="w-3.5 h-3.5 mr-1" /> Share
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteOpp(o.opportunity_id)}
                          className="border-red-700/50 text-red-400 hover:bg-red-500/10 rounded-sm"
                          data-testid={`delete-${o.opportunity_id}`}
                        >
                          <TrashSimple className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setInterestOpp(o)}
                          className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm flex-1"
                          data-testid={`interest-${o.opportunity_id}`}
                        >
                          <Buildings className="w-3.5 h-3.5 mr-1" /> I'm interested
                          <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => shareOpp(o.opportunity_id)}
                          className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm"
                          data-testid={`share-${o.opportunity_id}`}
                          title="Share this opportunity"
                        >
                          <ShareNetwork className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                  {o.interest_count > 0 && tab === "mine" && (
                    <p className="text-[10px] text-emerald-400 font-mono">{o.interest_count} {o.interest_count === 1 ? "business has" : "businesses have"} expressed interest</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Post Opportunity modal */}
        <Dialog open={showPost} onOpenChange={setShowPost}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
            <DialogHeader>
              <DialogTitle>Post a Sponsorship Opportunity</DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">Local businesses and franchisees will browse this. Be specific — concrete events, deadlines, and benefits convert.</p>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div><Label className="text-xs">Organization name</Label>
                <Input value={form.org_name} onChange={(e) => setForm({...form, org_name: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-org-name" /></div>
              <div><Label className="text-xs">Event or program name</Label>
                <Input value={form.event_name} onChange={(e) => setForm({...form, event_name: e.target.value})} placeholder="e.g. Annual Spring Mentoring Gala" className="bg-zinc-900 border-zinc-800" data-testid="form-event" /></div>
              <div><Label className="text-xs">Description (50–800 chars)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={4} placeholder="Who you serve, why this event matters, what a sponsor's dollars will fund..." className="bg-zinc-900 border-zinc-800 text-sm" data-testid="form-description" />
                <p className="text-[10px] text-zinc-600 mt-0.5">{form.description.length}/800</p></div>
              <div><Label className="text-xs">Category <span className="text-zinc-600">(helps small orgs surface in their niche)</span></Label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm" data-testid="form-category">
                  {SPONSORSHIP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">County</Label>
                  <select value={form.county} onChange={(e) => setForm({...form, county: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm" data-testid="form-county">
                    <option value="">Pick a county</option>
                    {SC_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><Label className="text-xs">Deadline</Label>
                  <Input type="date" value={form.deadline} onChange={(e) => setForm({...form, deadline: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-deadline" /></div>
                <div><Label className="text-xs">Min sponsorship ($)</Label>
                  <Input type="number" min="50" value={form.sponsorship_min} onChange={(e) => setForm({...form, sponsorship_min: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-min" /></div>
                <div><Label className="text-xs">Max sponsorship ($)</Label>
                  <Input type="number" min="50" value={form.sponsorship_max} onChange={(e) => setForm({...form, sponsorship_max: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-max" /></div>
              </div>
              <div><Label className="text-xs">Sponsor benefits (one per line)</Label>
                <Textarea
                  value={form.benefits_offered.join("\n")}
                  onChange={(e) => setForm({...form, benefits_offered: e.target.value.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 8)})}
                  rows={3}
                  className="bg-zinc-900 border-zinc-800 text-sm"
                  placeholder="Logo on event signage\nSocial media spotlight\nNamed scholarship for one year"
                  data-testid="form-benefits"
                /></div>
              <div><Label className="text-xs">Industries you'd welcome (one per line, optional)</Label>
                <Textarea
                  value={form.industries_welcomed.join("\n")}
                  onChange={(e) => setForm({...form, industries_welcomed: e.target.value.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 8)})}
                  rows={2}
                  className="bg-zinc-900 border-zinc-800 text-sm"
                  placeholder="Banking & Finance\nHealthcare\nRestaurant & Food Service"
                  data-testid="form-industries"
                /></div>
              <div><Label className="text-xs">Contact email (where business inquiries go)</Label>
                <Input value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="form-contact" /></div>
              <Button onClick={submitPost} disabled={postLoading} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="form-submit">
                {postLoading ? "Posting…" : "Post Opportunity"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Express Interest modal */}
        <Dialog open={!!interestOpp} onOpenChange={(o) => { if (!o) setInterestOpp(null); }}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md w-[calc(100vw-2rem)]" data-testid="interest-modal">
            {interestOpp && (
              <>
                <DialogHeader>
                  <DialogTitle>Express interest in {interestOpp.event_name}</DialogTitle>
                  <p className="text-xs text-zinc-500 mt-1">We'll email {interestOpp.org_name} your contact details. They'll reach out directly to negotiate.</p>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div><Label className="text-xs">Your business name</Label>
                    <Input value={interestForm.business_name} onChange={(e) => setInterestForm({...interestForm, business_name: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="interest-business-name" /></div>
                  <div><Label className="text-xs">Your email</Label>
                    <Input type="email" value={interestForm.business_email} onChange={(e) => setInterestForm({...interestForm, business_email: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="interest-email" /></div>
                  <div><Label className="text-xs">Phone (optional)</Label>
                    <Input value={interestForm.business_phone} onChange={(e) => setInterestForm({...interestForm, business_phone: e.target.value})} className="bg-zinc-900 border-zinc-800" data-testid="interest-phone" /></div>
                  <div><Label className="text-xs">Proposed sponsorship amount (optional)</Label>
                    <Input value={interestForm.proposed_tier} onChange={(e) => setInterestForm({...interestForm, proposed_tier: e.target.value})} placeholder={`Within $${interestOpp.sponsorship_min.toLocaleString()}–$${interestOpp.sponsorship_max.toLocaleString()}`} className="bg-zinc-900 border-zinc-800" data-testid="interest-tier" /></div>
                  <div><Label className="text-xs">Message (optional)</Label>
                    <Textarea value={interestForm.message} onChange={(e) => setInterestForm({...interestForm, message: e.target.value})} rows={3} className="bg-zinc-900 border-zinc-800 text-sm" placeholder="A line or two on why this sponsorship fits your business" data-testid="interest-message" /></div>
                  <Button onClick={submitInterest} disabled={interestLoading} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="interest-submit">
                    {interestLoading ? "Sending…" : `Send to ${interestOpp.org_name}`}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Sponsorships;
